'use client';

import { jsx, jsxs } from "react/jsx-runtime";
import { memo, forwardRef } from "react";
import { absoluteCentered } from "../../../css-utils.js";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner.js";
import { Text } from "../Text/Text.js";
import { Check } from "../../../react-icons.js";
import { CustomAnchor } from "../../quarks/CustomAnchor/CustomAnchor.js";
import { newWindowIndication, defaultDoneLabel } from "./Button.data.js";
import { overlayFgColorAttr } from "./utils/constants.js";
import { getAnchorContainerClassNames } from "./utils/getAnchorContainerClassNames.js";
import { getAnchorContentClassNames } from "./utils/getAnchorContentClassNames.js";
import { getButtonContainerClassNames } from "./utils/getButtonContainerClassNames.js";
import { getButtonContentClassNames } from "./utils/getButtonContentClassNames.js";
import { getIconSize } from "./utils/getIconSize.js";
import { processAnchorProps } from "./utils/processAnchorProps.js";
import { processButtonProps } from "./utils/processButtonProps.js";
import { useRuntimeOverlays } from "./utils/useRuntimeOverlays.js";
import { successIcon } from "./Button.css.js";
function ButtonComponent(props, ref) {
  if ("href" in props && typeof props.href !== "undefined") {
    return renderAsAnchor(props, ref);
  }
  return renderAsButton(props, ref);
}
function renderAsAnchor(props, refArg) {
  const p = processAnchorProps(props);
  const ref = useRuntimeOverlays(refArg, p);
  return /* @__PURE__ */ jsx(
    CustomAnchor,
    {
      "data-qa": p.dataQa,
      ...p.propsToSpread,
      className: getAnchorContainerClassNames(p),
      href: p.href,
      ref,
      target: p.target,
      children: /* @__PURE__ */ jsxs("span", { className: getAnchorContentClassNames(p), children: [
        p.iconPosition === "lead" && p.iconJsx,
        /* @__PURE__ */ jsx(
          Text,
          {
            color: p.textColor,
            variant: `label.${p.size}`,
            visuallyHidden: p.hiddenLabel && !!p.Icon,
            ...{ [overlayFgColorAttr]: true },
            children: p.children
          }
        ),
        p.target === "_blank" && /* @__PURE__ */ jsx(Text, { visuallyHidden: true, children: newWindowIndication }),
        p.hasA11yLabel && /* @__PURE__ */ jsx(Text, { visuallyHidden: true, children: p.a11yLabel }),
        p.iconPosition !== "lead" && p.iconJsx
      ] })
    }
  );
}
function renderAsButton(rawProps, refArg) {
  const p = processButtonProps(rawProps);
  const ref = useRuntimeOverlays(refArg, p);
  let spinnerJsx = null;
  let doneJsx = null;
  const isLoading = !!p.isLoading && !p.isSuccess;
  const isSuccess = !!p.isSuccess;
  const isInteractive = !isLoading && !isSuccess && !p.disabled;
  if (isLoading) {
    spinnerJsx = /* @__PURE__ */ jsx("span", { className: absoluteCentered, children: /* @__PURE__ */ jsx(
      LoadingSpinner,
      {
        a11yLabel: p.a11yLoadingLabel,
        size: p.loadingSize,
        onDark: !p.disabled && p.isLoadingSpinnerOnDark
      }
    ) });
  }
  if (isSuccess) {
    doneJsx = /* @__PURE__ */ jsxs("span", { className: absoluteCentered, children: [
      /* @__PURE__ */ jsx(Text, { visuallyHidden: true, children: p.a11ySuccessLabel || defaultDoneLabel }),
      /* @__PURE__ */ jsx(
        Check,
        {
          className: successIcon,
          size: getIconSize({ size: p.size })
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs(
    "button",
    {
      "data-qa": p.dataQa,
      ...p.propsToSpread,
      "aria-disabled": p.disabled || isLoading || void 0,
      "aria-live": !isInteractive ? "polite" : void 0,
      className: getButtonContainerClassNames(p),
      ref,
      type: p.type === "submit" ? "submit" : "button",
      onClick: !isInteractive ? (e) => e.preventDefault() : p.onClick,
      children: [
        spinnerJsx,
        doneJsx,
        /* @__PURE__ */ jsxs("span", { className: getButtonContentClassNames(p), children: [
          p.iconPosition === "lead" && p.iconJsx,
          /* @__PURE__ */ jsx(
            Text,
            {
              "aria-hidden": p.hasA11yLabel || isSuccess || isLoading ? "true" : void 0,
              color: p.textColor,
              variant: `label.${p.size}`,
              visuallyHidden: p.isLabelHidden,
              ...{ [overlayFgColorAttr]: true },
              children: p.children
            }
          ),
          p.hasA11yLabel && !isSuccess && !isLoading && /* @__PURE__ */ jsx(Text, { visuallyHidden: true, children: p.a11yLabel }),
          p.iconPosition !== "lead" && p.iconJsx
        ] })
      ]
    }
  );
}
const Button = /* @__PURE__ */ memo(forwardRef(ButtonComponent));
export {
  Button
};
